import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  BookOpen, Users, CalendarDays, CreditCard, Clock, MessageSquare,
  UserPlus, Shield, ArrowRight, CheckCircle2, AlertTriangle, Building2, Mail
} from "lucide-react";

function Section({ icon: Icon, title, children, id }) {
  return (
    <section id={id} className="scroll-mt-24">
      <div className="flex items-center gap-3 mb-4">
        <div className="h-9 w-9 rounded-lg bg-blue-500/10 flex items-center justify-center">
          <Icon className="h-5 w-5 text-blue-600" />
        </div>
        <h2 className="text-xl font-heading font-bold text-rig-charcoal">{title}</h2>
      </div>
      <div className="text-sm text-rig-charcoal/80 leading-relaxed space-y-3 pl-12">
        {children}
      </div>
    </section>
  );
}

function Step({ n, children }) {
  return (
    <div className="flex gap-3 items-start">
      <span className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-600 text-white text-xs font-bold flex items-center justify-center mt-0.5">{n}</span>
      <span>{children}</span>
    </div>
  );
}

const TOC = [
  { id: "overview", label: "Getting Started", icon: BookOpen },
  { id: "signin", label: "Sign-In Options", icon: Shield },
  { id: "team", label: "Managing Your Team", icon: Users },
  { id: "bookings", label: "Team Bookings", icon: CalendarDays },
  { id: "guests", label: "Guest Registration", icon: UserPlus },
  { id: "plan", label: "Your Plan", icon: CreditCard },
  { id: "messages", label: "Messages & Inbox", icon: MessageSquare },
  { id: "cancellation", label: "Cancellation Policy", icon: AlertTriangle },
];

export default function ManagerGuide() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    document.title = "Manager Guide | The Rig";
    if (!authLoading && (!user || user.role !== "manager")) navigate("/login");
  }, [user, authLoading, navigate]);

  if (authLoading) return null;

  return (
    <div className="max-w-5xl mx-auto px-6 py-12" data-testid="manager-guide">
      <title>Manager Guide | The Rig</title>

      <div className="mb-10">
        <Badge className="bg-blue-100 text-blue-800 border-0 mb-3">Manager Documentation</Badge>
        <h1 className="text-3xl sm:text-4xl font-heading font-extrabold text-rig-charcoal mb-2">
          Manager Guide
        </h1>
        <p className="text-rig-charcoal/60 text-base max-w-2xl">
          Everything you need to manage your team, book spaces, and make the most of your membership plan at The Rig.
        </p>
      </div>

      <div className="flex flex-col lg:flex-row gap-10">
        <nav className="lg:w-52 flex-shrink-0">
          <Card className="p-4 lg:sticky lg:top-24">
            <p className="text-xs font-bold uppercase tracking-wider text-rig-charcoal/40 mb-3">Contents</p>
            <ul className="space-y-1">
              {TOC.map(t => (
                <li key={t.id}>
                  <a href={`#${t.id}`} className="flex items-center gap-2 text-sm text-rig-charcoal/70 hover:text-blue-600 py-1 transition-colors">
                    <t.icon className="h-3.5 w-3.5" />
                    {t.label}
                  </a>
                </li>
              ))}
            </ul>
          </Card>
        </nav>

        <div className="flex-1 space-y-10">
          <Section icon={BookOpen} title="Getting Started" id="overview">
            <p>
              As a <strong>Manager</strong> at The Rig, you oversee a team of members under your company's membership plan. You can add team members, view their bookings, and communicate with admins — all from your dashboard.
            </p>
            <p>Access your dashboard from the <strong>"Manager"</strong> link in the navigation bar, or go to <code className="bg-rig-charcoal/5 px-1.5 py-0.5 rounded text-xs">/manager</code>.</p>
            <p>Your dashboard has <strong>4 tabs</strong>:</p>
            <div className="grid grid-cols-2 gap-2 mt-1">
              {[
                { tab: "Overview", desc: "Stats and recent activity" },
                { tab: "Team", desc: "Add and manage members" },
                { tab: "Bookings", desc: "View all team bookings" },
                { tab: "Messages", desc: "Send notes to admin" },
              ].map(t => (
                <div key={t.tab} className="bg-rig-charcoal/5 rounded-lg p-3">
                  <p className="font-semibold text-sm text-rig-charcoal">{t.tab}</p>
                  <p className="text-xs text-rig-charcoal/60 mt-0.5">{t.desc}</p>
                </div>
              ))}
            </div>
          </Section>

          <Separator />

          <Section icon={Shield} title="Sign-In Options" id="signin">
            <p>You can sign in to The Rig in two ways:</p>
            <ul className="list-disc ml-4 space-y-1">
              <li><strong>Email & password</strong> — use the credentials you received by email when your account was created.</li>
              <li><strong>One-time passcode</strong> — on the login page, click <strong>"Sign in with passcode"</strong>, enter your email, and we'll send a 6-digit code that expires in 5 minutes. No password needed.</li>
            </ul>

            <p className="mt-2"><strong>When you add a team member:</strong></p>
            <p>They automatically receive a <strong>welcome email</strong> and a separate <strong>credentials email</strong> with their login details, so you don't need to share passwords manually.</p>
          </Section>

          <Separator />

          <Section icon={Users} title="Managing Your Team" id="team">
            <p><strong>Adding a team member:</strong></p>
            <Step n={1}>Go to the <strong>Team</strong> tab on your dashboard.</Step>
            <Step n={2}>Click <strong>"Add Member"</strong> and fill in their name, email, password, and phone number.</Step>
            <Step n={3}>The new member is automatically linked to your company plan and can start booking immediately.</Step>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-2">
              <p className="font-semibold text-blue-800 text-xs flex items-center gap-1"><Users className="h-3.5 w-3.5" /> Member Quota</p>
              <p className="text-blue-700 mt-1">You have a maximum number of members you can add (set by admin). Your current quota is shown on the Overview tab.</p>
            </div>

            <p className="mt-2"><strong>Removing a member:</strong></p>
            <p>Click the delete icon next to a team member. Their account reverts to a "guest" role — they lose plan benefits but keep their account.</p>
          </Section>

          <Separator />

          <Section icon={CalendarDays} title="Team Bookings" id="bookings">
            <p>The <strong>Bookings</strong> tab shows all bookings made by your team members.</p>
            <ul className="list-disc ml-4 space-y-1">
              <li>View booking date, time, space, and status at a glance</li>
              <li>Track which team members are using which rooms</li>
              <li>Bookings covered by your plan show as <Badge className="bg-green-100 text-green-800 border-0 text-xs">confirmed</Badge></li>
              <li>Any excess bookings beyond plan hours require payment</li>
              <li><strong>Edit</strong> any team booking using the pencil icon — change meeting name, date, time, or notes</li>
              <li><strong>Cancel</strong> any team booking using the X button</li>
            </ul>
            <p className="mt-2"><strong>How to book a room:</strong></p>
            <Step n={1}>Navigate to <strong>Spaces</strong> from the top navigation.</Step>
            <Step n={2}>Select a meeting room and click <strong>"Book Now"</strong>.</Step>
            <Step n={3}>Choose your date, time slots, and preferred layout.</Step>
            <Step n={4}>Add a <strong>Meeting Name</strong> (optional) — shown on room displays and in emails.</Step>
            <Step n={5}>Use the <strong>"Booking For"</strong> dropdown to book on behalf of any team member. You and the member both receive confirmation emails.</Step>
            <Step n={6}>Choose your preferred room layout (boardroom, classroom, theater, U-shape, banquet).</Step>
            <Step n={7}>Confirm your booking. If plan hours are available, it's confirmed instantly.</Step>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-2">
              <p className="font-semibold text-blue-800 text-xs flex items-center gap-1"><Users className="h-3.5 w-3.5" /> Booking on Behalf</p>
              <p className="text-blue-700 mt-1">As a manager, you can book rooms for any of your team members. The booking shows who it was booked for on the dashboard, room display, and in emails. Hours are deducted from the team's shared plan.</p>
            </div>
          </Section>

          <Separator />

          <Section icon={UserPlus} title="Guest Registration" id="guests">
            <p>You can pre-register guests at any time — no booking required. Use the <strong>"Register Guest"</strong> button on your dashboard.</p>
            <Step n={1}>Click <strong>"Register Guest"</strong> at the top of your dashboard.</Step>
            <Step n={2}>Enter the guest's name, email (optional), and purpose.</Step>
            <Step n={3}>If an email is provided, the guest receives a <strong>QR code</strong> they can scan at the lobby kiosk to check in instantly.</Step>

            <p className="mt-2"><strong>During a booking:</strong></p>
            <p>In step 3 of any booking, find <strong>"Pre-register Guests"</strong> to add visitors to a specific meeting. Each guest gets an email invite with room and time details plus a QR check-in code.</p>

            <p className="mt-2"><strong>At the kiosk:</strong></p>
            <p>Pre-registered guests can check in at the lobby kiosk by scanning their QR code or entering their check-in code. You'll receive an email when they arrive.</p>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-2">
              <p className="font-semibold text-blue-800 text-xs flex items-center gap-1"><UserPlus className="h-3.5 w-3.5" /> Saved/Frequent Guests</p>
              <p className="text-blue-700 mt-1">Every guest you register is <strong>automatically saved</strong> to your contact book. Use the <strong>"Quick add from frequent guests"</strong> search in the booking flow to add repeat visitors in one click — no retyping.</p>
            </div>
          </Section>

          <Separator />

          <Section icon={CreditCard} title="Your Plan" id="plan">
            <p>Your company plan determines what your team can do:</p>
            <ul className="list-disc ml-4 space-y-1">
              <li><strong>Included hours</strong> — monthly hours covered (shared across your team)</li>
              <li><strong>Room access</strong> — which rooms your team can book</li>
              <li><strong>Member quota</strong> — max number of team members</li>
            </ul>
            <p className="mt-2">You can view your current plan details and used hours on the <strong>Overview</strong> tab. All prices are in <Badge variant="outline" className="text-xs mx-1">XCG</Badge>.</p>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mt-2">
              <p className="font-semibold text-amber-800 text-xs flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> Hour Tracking</p>
              <p className="text-amber-700 mt-1">Plan hours are deducted when a team member books. If all plan hours are used up, additional bookings will require separate payment.</p>
            </div>
          </Section>

          <Separator />

          <Section icon={MessageSquare} title="Messages & Inbox" id="messages">
            <p><strong>Admin broadcasts:</strong></p>
            <p>When an admin sends a broadcast message, it appears in your <strong>Messages tab</strong> with an unread badge. You'll also receive it via email. Click <strong>"Mark as read"</strong> to dismiss.</p>

            <p className="mt-2"><strong>Messaging admin:</strong></p>
            <p>Use the <strong>Messages</strong> tab to communicate directly with The Rig's admin team.</p>
            <Step n={1}>Go to the Messages tab on your dashboard.</Step>
            <Step n={2}>Type your message and select the type (note, request, or issue).</Step>
            <Step n={3}>Click <strong>"Send"</strong>. Admin will reply and you'll see their response in the thread.</Step>
            <p className="mt-2">Use messages for: plan questions, special requests, reporting facility issues, or scheduling discussions.</p>
          </Section>

          <Separator />

          <Section icon={AlertTriangle} title="Cancellation Policy" id="cancellation">
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="font-semibold text-red-800 text-sm">12-Hour Rule</p>
              <ul className="list-disc ml-4 space-y-1 text-red-700 mt-1">
                <li>Cancel <strong>more than 12 hours</strong> before start → instant cancellation, hours refunded.</li>
                <li>Cancel <strong>less than 12 hours</strong> before start for <strong>paid bookings</strong> → requires admin approval.</li>
                <li>Unpaid bookings can always be cancelled immediately.</li>
              </ul>
            </div>
            <p className="mt-2">To edit or cancel, go to your <strong>Bookings</strong> tab and use the pencil (edit) or X (cancel) buttons on the relevant booking.</p>
          </Section>

          <Separator />

          <div className="text-center py-8 text-rig-charcoal/40 text-sm">
            <p>Need help? Send a message to admin from your dashboard, or email <a href="mailto:info@ic-foundation.org" className="text-blue-600 underline">info@ic-foundation.org</a></p>
          </div>
        </div>
      </div>
    </div>
  );
}
